import { WSElement, WSText } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useQueryPayables } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import {
  PayablesFilters,
  PayablesStatusFilter,
  PayablesTable,
  usePayablesQuery
} from "../../components/NewPayablesTable/PayablesTable";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { PayableActionBlock } from "../../components/PayableActionBlock/PayableActionBlock";
import { API_QUERY_PAYABLES_TO_APPROVE } from "../../utils/payables/apiQueries";
import { useApproveAll } from "../../utils/payables/approveAll";

const DEFAULT_FILTERS: PayablesFilters = {
  status: [PayablesStatusFilter.ToApprove]
};

export const PayablesToApprove: React.FC = () => {
  useBrowserPageTitle("Payables To Approve");
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();

  const queryPayablesDraft = usePayablesQuery({
    status: [PayablesStatusFilter.Draft]
  });

  const queryPayablesToApprove = useQueryPayables({
    ...API_QUERY_PAYABLES_TO_APPROVE,
    page: {
      size: 1
    }
  });

  const { act: approveAll, isLoading: approveAllIsLoading } = useApproveAll();

  return (
    <>
      <WSText.Heading1 mb="M">To Approve</WSText.Heading1>
      <WSText color="gray600" mb="2XL">
        All payables and requests that are not yet scheduled to be paid.
      </WSText>

      <WSElement mb="2XL">
        <WSQueries
          queries={{ queryPayablesDraft, queryPayablesToApprove }}
          renderErrors={() => null}
        >
          {({
            queryPayablesDraft: {
              data: { summary: draftPayablesSummary }
            },
            queryPayablesToApprove: {
              data: { summary: toApprovePayablesSummary }
            }
          }) => (
            <>
              {toApprovePayablesSummary.listSize > 1 && (
                <PayableActionBlock
                  mb="XL"
                  icon="dollar-circle"
                  rightButton={{
                    children: `Approve ${toApprovePayablesSummary.listSize} payments`,
                    loading: approveAllIsLoading,
                    onClick: approveAll
                  }}
                  onClick={approveAll}
                >
                  <WSText color="gray500" mb="XS">
                    <WSText as="b" inline color="gray700">
                      Pending approval:
                    </WSText>{" "}
                    {toApprovePayablesSummary.listSize} payables totaling{" "}
                    {formatMoney(
                      toApprovePayablesSummary.totalValue,
                      defaultCurrency
                    )}
                    .
                  </WSText>
                </PayableActionBlock>
              )}

              {draftPayablesSummary.listSize > 0 && (
                <PayableActionBlock
                  onClick={() => {
                    history.push("/member/invoices/payables/draft");
                  }}
                  icon="alert-circle"
                  contentButton={{
                    children: "Manage draft payables"
                  }}
                >
                  <WSText color="gray500">
                    <WSText as="b" color="gray700">
                      You have {draftPayablesSummary.listSize} draft payable
                      {draftPayablesSummary.listSize > 1 ? "s" : ""}.
                    </WSText>
                  </WSText>
                </PayableActionBlock>
              )}
            </>
          )}
        </WSQueries>
      </WSElement>

      <PayablesTable
        bulkActions={["approve", "delete"]}
        visibleFilters={{
          payee: true,
          collaboratorReviewStatus: true,
          openedAtRange: true
        }}
        basePath="/member/invoices/payables/to-approve"
        defaultFilters={DEFAULT_FILTERS}
        showHeaders
        showInvoiceNumber
        showDateRequested
        showDueDate
        emptyState={{
          firstTime:
            "Every requested, scheduled, and paid payable will display here.",
          standart:
            "You’re all caught up! Nothing is waiting for your approval."
        }}
      />
    </>
  );
};
